import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, FileStack, Loader2, Phone, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { money, shortDate } from "@/lib/invoice";
import { Button } from "@/components/ui/button";
import EmailStatementButton from "@/components/EmailStatementButton";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function CustomerCard({ c, i, print }) {
  const { isAdmin } = useAuth();
  const tel = (c.phone || "").replace(/[^\d+]/g, "");
  return (
    <div className={`border rounded-lg p-4 break-inside-avoid ${print ? "border-slate-300" : "bg-card"}`} data-testid={`reminder-customer-${i}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold" data-testid={`reminder-name-${i}`}>{c.name}</p>
          <p className={`text-xs ${print ? "text-slate-600" : "text-muted-foreground"}`}>{[c.city_state_zip, c.contact && `Contact: ${c.contact}`, c.administrator && `Admin: ${c.administrator}`].filter(Boolean).join(" · ")}</p>
        </div>
        <div className="text-right">
          <p className="font-mono font-bold" data-testid={`reminder-total-${i}`}>{money(c.total)}</p>
          {!print && <div className="flex flex-col items-end gap-0.5">
            <button onClick={() => window.open(`/statement?customer=${encodeURIComponent(c.name)}`, "_blank")} className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid={`reminder-statement-${i}`}><Printer size={12} /> Statement</button>
            {isAdmin && <EmailStatementButton customer={c} kind="reminder" compact testId={`reminder-email-${i}`} />}
          </div>}
          {tel && (print ? <p className="text-sm">{c.phone}</p>
            : <a href={`tel:${tel}`} className="inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid={`reminder-call-${i}`}><Phone size={13} /> {c.phone}</a>)}
        </div>
      </div>
      <table className="w-full text-sm mt-3">
        <tbody>
          {c.invoices.map((inv) => (
            <tr key={inv.id} className={`border-t ${print ? "border-slate-200" : ""}`}>
              <td className="py-1.5 font-mono">{print ? inv.number : <Link to={`/invoices/${inv.id}`} className="text-primary hover:underline">{inv.number}</Link>}</td>
              <td className="py-1.5">{shortDate(inv.service_date)}</td>
              <td className={`py-1.5 ${inv.days > 60 ? "text-red-500" : "text-amber-500"}`}>{inv.days} days</td>
              <td className="py-1.5 text-right font-mono">{money(inv.grand_total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Reminders({ print = false }) {
  const [search] = useSearchParams();
  const [days, setDays] = useState(["30", "60", "90"].includes(search.get("days")) ? search.get("days") : "30");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reminders", { params: { days } }).then((r) => setData(r.data)).catch((e) => toast.error(formatError(e)));
  }, [days]);
  useEffect(() => {
    if (!print || !data) return undefined;
    const t = setTimeout(() => window.print(), 600);
    return () => clearTimeout(t);
  }, [print, data]);

  const head = data && `${data.customers.length} customer${data.customers.length === 1 ? "" : "s"} · ${data.count} invoice${data.count === 1 ? "" : "s"} · ${money(data.total)} owed`;
  if (print) {
    return (
      <div className="min-h-screen bg-white text-black p-8" data-testid="reminders-print-page">
        <h1 className="text-2xl font-bold">Unpaid invoices over {days} days</h1>
        <p className="text-sm text-slate-600 mt-1">{new Date().toLocaleDateString()} · {head}</p>
        <div className="mt-6 space-y-4">{data ? data.customers.map((c, i) => <CustomerCard key={i} c={c} i={i} print />) : "Loading…"}</div>
      </div>
    );
  }
  return (
    <div className="fade-up" data-testid="reminders-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-cyan-400">Collections</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">Unpaid reminders</h1>
          <p className="text-sm text-muted-foreground mt-2" data-testid="reminders-summary">{head || "Loading…"}</p>
        </div>
        <div className="flex items-end gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-40" data-testid="reminders-days-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card">
              {["30", "60", "90"].map((d) => <SelectItem key={d} value={d} data-testid={`reminders-days-${d}`}>Over {d} days</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => window.open("/statement?all=1", "_blank")} data-testid="print-all-statements-button" className="hover:bg-secondary hover:text-foreground"><FileStack size={16} /> All statements</Button>
          <Button variant="outline" disabled={!data?.customers.length} onClick={() => window.open(`/reminders/print?days=${days}`, "_blank")} data-testid="reminders-print-button" className="hover:bg-secondary hover:text-foreground"><Printer size={16} /> Print list</Button>
        </div>
      </div>
      <div className="mt-6 grid xl:grid-cols-2 gap-4" data-testid="reminders-list">
        {!data && <Loader2 className="animate-spin text-primary" />}
        {data?.customers.map((c, i) => <CustomerCard key={i} c={c} i={i} />)}
        {data?.customers.length === 0 && (
          <p className="text-muted-foreground flex items-center gap-2" data-testid="reminders-empty"><CheckCircle2 size={16} className="text-emerald-400" /> No unpaid invoices older than {days} days.</p>
        )}
      </div>
    </div>
  );
}
